import React, { useState, useEffect } from 'react';
import axios from 'axios';
import NotificationModal from '../shared/NotificationModal';
import { OverrideDefinition } from '../../types';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Editable row state
interface EditableOverrideDefinition extends OverrideDefinition {
  isNew?: boolean;
  isSaving?: boolean;
}

const OverrideDefinitionsManager: React.FC = () => {
  const [definitions, setDefinitions] = useState<EditableOverrideDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationType, setNotificationType] = useState<'success' | 'error' | 'info'>('info');
  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [missingKeysByWorkflow, setMissingKeysByWorkflow] = useState<{ [workflow: string]: string[] }>({});
  const [loadingMissingKeys, setLoadingMissingKeys] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; key: string } | null>(null);
  const [deletePassword, setDeletePassword] = useState('');
  const [editingRowBackup, setEditingRowBackup] = useState<EditableOverrideDefinition | null>(null);
  const [sortColumn, setSortColumn] = useState<keyof EditableOverrideDefinition>('id');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    fetchDefinitions();
    fetchMissingKeys();
  }, []);

  const fetchDefinitions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get<OverrideDefinition[]>(`${API_BASE}/override-definitions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDefinitions(response.data);
    } catch (error: any) {
      console.error('Failed to fetch override definitions:', error);
      showNotificationMessage('Failed to fetch override definitions', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchMissingKeys = async () => {
    try {
      setLoadingMissingKeys(true);
      const token = localStorage.getItem('token');
      const response = await axios.get<{ missing_keys_by_workflow: { [workflow: string]: string[] } }>(
        `${API_BASE}/override-definitions/missing-keys`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMissingKeysByWorkflow(response.data.missing_keys_by_workflow);
    } catch (error: any) {
      console.error('Failed to fetch missing keys:', error);
    } finally {
      setLoadingMissingKeys(false);
    }
  };

  const showNotificationMessage = (message: string, type: 'success' | 'error' | 'info') => {
    setNotificationMessage(message);
    setNotificationType(type);
    setShowNotification(true);
  };

  const handleAddNew = () => {
    const newDefinition: EditableOverrideDefinition = {
      id: Date.now(),
      override_key: '',
      display_name: '',
      description: '',
      data_type: 'string',
      validation_rules: {},
      category: '',
      created_at: new Date().toISOString(),
      isNew: true
    };
    setDefinitions([newDefinition, ...definitions]);
  };

  const handleCellClick = (id: number, field: string) => {
    const definition = definitions.find(d => d.id === id);
    if (definition?.isSaving) return; // Don't allow editing while saving

    // If already editing a different row, don't allow
    if (editingRowId !== null && editingRowId !== id) {
      return;
    }

    // Backup the current row data before editing (only once per row)
    if (editingRowId === null) {
      setEditingRowBackup(definition ? { ...definition } : null);
      setEditingRowId(id);
    }
  };

  const handleCancelEdit = () => {
    if (editingRowId !== null && editingRowBackup) {
      // Restore the backed up data
      setDefinitions(definitions.map(d =>
        d.id === editingRowId ? editingRowBackup : d
      ));
    } else if (editingRowId !== null) {
      // If it's a new row with no data, remove it
      const definition = definitions.find(d => d.id === editingRowId);
      if (definition?.isNew && !definition.override_key.trim()) {
        setDefinitions(definitions.filter(d => d.id !== editingRowId));
      }
    }
    setEditingRowId(null);
    setEditingRowBackup(null);
  };

  const handleConfirmEdit = async () => {
    if (editingRowId === null) return;

    const definition = definitions.find(d => d.id === editingRowId);
    if (!definition) return;

    await handleSave(definition);
    setEditingRowId(null);
    setEditingRowBackup(null);
  };

  const handleSave = async (definition: EditableOverrideDefinition) => {
    // Skip empty new rows
    if (definition.isNew && !definition.override_key.trim()) {
      setDefinitions(definitions.filter(d => d.id !== definition.id));
      return;
    }

    try {
      setDefinitions(definitions.map(d =>
        d.id === definition.id ? { ...d, isSaving: true } : d
      ));

      const token = localStorage.getItem('token');
      const payload = {
        override_key: definition.override_key,
        display_name: definition.display_name,
        description: definition.description,
        data_type: definition.data_type,
        validation_rules: definition.validation_rules,
        category: definition.category
      };

      if (definition.isNew) {
        await axios.post(`${API_BASE}/override-definitions`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showNotificationMessage('Override definition created successfully!', 'success');
      } else {
        await axios.put(`${API_BASE}/override-definitions/${definition.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showNotificationMessage('Override definition updated successfully!', 'success');
      }

      fetchDefinitions();
      fetchMissingKeys(); // Refresh missing keys after save
    } catch (error: any) {
      console.error('Failed to save override definition:', error);
      showNotificationMessage(
        error.response?.data?.error || 'Failed to save override definition',
        'error'
      );
      setDefinitions(definitions.map(d =>
        d.id === definition.id ? { ...d, isSaving: false } : d
      ));
    }
  };

  const handleDelete = (id: number) => {
    const definition = definitions.find(d => d.id === id);

    // If it's a new unsaved row, just remove it
    if (definition?.isNew) {
      setDefinitions(definitions.filter(d => d.id !== id));
      return;
    }

    // Show password confirmation modal for existing entries
    if (definition) {
      setDeleteTarget({ id: definition.id, key: definition.override_key });
      setDeletePassword('');
      setShowDeleteModal(true);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    if (!deletePassword.trim()) {
      showNotificationMessage('Please enter the delete confirmation password', 'error');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE}/override-definitions/${deleteTarget.id}`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { confirmPassword: deletePassword }
      });
      setShowDeleteModal(false);
      setDeleteTarget(null);
      setDeletePassword('');
      showNotificationMessage('Override definition deleted successfully!', 'success');
      fetchDefinitions();
      fetchMissingKeys(); // Refresh missing keys after delete
    } catch (error: any) {
      console.error('Failed to delete override definition:', error);
      showNotificationMessage(
        error.response?.data?.error || 'Failed to delete override definition',
        'error'
      );
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeleteTarget(null);
    setDeletePassword('');
  };

  const handleFieldChange = (id: number, field: keyof EditableOverrideDefinition, value: any) => {
    setDefinitions(definitions.map(def =>
      def.id === id ? { ...def, [field]: value } : def
    ));
  };

  const parseValidationRules = (value: string): any => {
    try {
      return value ? JSON.parse(value) : {};
    } catch {
      return {};
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, definition: EditableOverrideDefinition, field: string) => {
    if (e.key === 'Enter' && field !== 'description') {
      e.preventDefault();
      handleConfirmEdit();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelEdit();
    }
  };

  const renderCell = (definition: EditableOverrideDefinition, field: keyof EditableOverrideDefinition, type: 'text' | 'select' | 'json' = 'text') => {
    const isEditing = editingRowId === definition.id;
    const value = definition[field];

    if (isEditing) {
      if (type === 'select') {
        return (
          <select
            value={value as string}
            onChange={(e) => handleFieldChange(definition.id, field, e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, definition, field)}
            className="w-full px-2 py-1 border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoFocus
          >
            <option value="string">string</option>
            <option value="integer">integer</option>
            <option value="float">float</option>
            <option value="boolean">boolean</option>
            <option value="select">select</option>
          </select>
        );
      } else if (type === 'json') {
        return (
          <input
            type="text"
            value={JSON.stringify(value || {})}
            onChange={(e) => handleFieldChange(definition.id, field, parseValidationRules(e.target.value))}
            onKeyDown={(e) => handleKeyDown(e, definition, field)}
            className="w-full px-2 py-1 border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-xs"
            placeholder='{"min": 512, "max": 2048}'
            autoFocus
          />
        );
      } else {
        return (
          <input
            type="text"
            value={value as string}
            onChange={(e) => handleFieldChange(definition.id, field, e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, definition, field)}
            className="w-full px-2 py-1 border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoFocus
          />
        );
      }
    }

    // Display mode
    const displayValue = type === 'json' ? JSON.stringify(value || {}) : (value as string);
    return (
      <div
        onClick={() => handleCellClick(definition.id, field)}
        className={`cursor-text hover:bg-gray-100 px-2 py-1 rounded min-h-[32px] ${
          definition.isSaving ? 'opacity-50 cursor-wait' : ''
        }`}
      >
        {displayValue || <span className="text-gray-400 italic">Click to edit</span>}
      </div>
    );
  };

  const handleCreateFromMissingKey = (key: string) => {
    const newDefinition: EditableOverrideDefinition = {
      id: Date.now(),
      override_key: key,
      display_name: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      description: '',
      data_type: 'string',
      validation_rules: {},
      category: '',
      created_at: new Date().toISOString(),
      isNew: true
    };
    setDefinitions([newDefinition, ...definitions]);
    setEditingRowBackup(newDefinition);
    setEditingRowId(newDefinition.id);
  };

  const handleSort = (column: keyof EditableOverrideDefinition) => {
    const newDirection = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortColumn(column);
    setSortDirection(newDirection);
  };

  const getSortedDefinitions = () => {
    const sorted = [...definitions].sort((a, b) => {
      const aVal = a[sortColumn];
      const bVal = b[sortColumn];

      // Handle null/undefined values
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      // Handle different data types
      if (sortColumn === 'id') {
        const aNum = typeof aVal === 'number' ? aVal : 0;
        const bNum = typeof bVal === 'number' ? bVal : 0;
        return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
      }

      if (sortColumn === 'validation_rules') {
        const aStr = JSON.stringify(aVal);
        const bStr = JSON.stringify(bVal);
        return sortDirection === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
      }

      // String comparison for all other fields
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      return sortDirection === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });

    return sorted;
  };

  const renderSortIcon = (column: keyof EditableOverrideDefinition) => {
    if (sortColumn !== column) {
      return <span className="ml-1 text-gray-400">⇅</span>;
    }
    return sortDirection === 'asc' ?
      <span className="ml-1 text-blue-600">↑</span> :
      <span className="ml-1 text-blue-600">↓</span>;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">Loading override definitions...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Override Definitions</h2>
          <p className="text-sm text-gray-600 mt-1">
            Manage override parameters for slug-based workflows. Click any cell to edit inline.
          </p>
        </div>
        <button
          onClick={handleAddNew}
          className="px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
        >
          <span className="text-lg">+</span>
          Add New Override
        </button>
      </div>

      {/* Missing Keys Section - Grouped by Workflow */}
      {Object.keys(missingKeysByWorkflow).length > 0 && (
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-yellow-900 mb-2 flex items-center gap-2">
            <span>⚠️</span>
            Missing Override Definitions from Base Workflows
          </h3>
          <p className="text-sm text-yellow-800 mb-4">
            These hygo_keys exist in base workflows but don't have override definitions yet. Click any key to create its definition.
          </p>

          <div className="space-y-4">
            {Object.entries(missingKeysByWorkflow).map(([workflowFile, keys]) => (
              <div key={workflowFile} className="bg-white border border-yellow-300 rounded-md p-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm font-semibold text-gray-700">📄</span>
                  <span className="text-sm font-semibold text-gray-700">{workflowFile}</span>
                  <span className="text-xs text-gray-500">({keys.length} missing)</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {keys.map((key) => (
                    <button
                      key={`${workflowFile}-${key}`}
                      onClick={() => handleCreateFromMissingKey(key)}
                      className="px-3 py-1 bg-yellow-100 border border-yellow-400 rounded-md text-sm font-mono text-yellow-900 hover:bg-yellow-200 transition-colors"
                      title={`Click to create override definition for ${key}`}
                    >
                      {key}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {loadingMissingKeys && Object.keys(missingKeysByWorkflow).length === 0 && (
        <div className="mb-6 text-sm text-gray-500 italic">
          Analyzing base workflows for missing hygo_keys...
        </div>
      )}

      {definitions.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          No override definitions found. Create your first one!
        </div>
      ) : (
        <div className="overflow-x-auto bg-white rounded-lg shadow">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  onClick={() => handleSort('id')}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                >
                  <div className="flex items-center">
                    ID {renderSortIcon('id')}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('override_key')}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                >
                  <div className="flex items-center">
                    Override Key {renderSortIcon('override_key')}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('display_name')}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                >
                  <div className="flex items-center">
                    Display Name {renderSortIcon('display_name')}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('description')}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                >
                  <div className="flex items-center">
                    Description {renderSortIcon('description')}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('data_type')}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                >
                  <div className="flex items-center">
                    Data Type {renderSortIcon('data_type')}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('validation_rules')}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                >
                  <div className="flex items-center">
                    Validation Rules {renderSortIcon('validation_rules')}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('category')}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                >
                  <div className="flex items-center">
                    Category {renderSortIcon('category')}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {getSortedDefinitions().map((def) => (
                <tr key={def.id} className={def.isNew ? 'bg-blue-50' : ''}>
                  <td className="px-4 py-2 whitespace-nowrap">
                    <span className="text-sm text-gray-600">
                      {def.isNew ? '-' : def.id}
                    </span>
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    <span className="text-sm font-mono text-gray-900">
                      {renderCell(def, 'override_key')}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <span className="text-sm text-gray-900">
                      {renderCell(def, 'display_name')}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <span className="text-sm text-gray-600">
                      {renderCell(def, 'description')}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <span className="text-sm font-mono text-gray-700">
                      {renderCell(def, 'data_type', 'select')}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <span className="text-xs font-mono text-gray-600">
                      {renderCell(def, 'validation_rules', 'json')}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <span className="text-sm text-gray-600">
                      {renderCell(def, 'category')}
                    </span>
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    {editingRowId === def.id ? (
                      <div className="flex gap-2">
                        <button
                          onClick={handleConfirmEdit}
                          disabled={def.isSaving}
                          className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded font-medium text-sm transition-colors"
                        >
                          Save
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          disabled={def.isSaving}
                          className="px-3 py-1 bg-gray-500 hover:bg-gray-600 text-white rounded font-medium text-sm transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleDelete(def.id)}
                        disabled={def.isSaving || editingRowId !== null}
                        className={`font-medium text-sm ${
                          def.isSaving || editingRowId !== null
                            ? 'text-gray-400 cursor-not-allowed'
                            : 'text-red-600 hover:text-red-800'
                        }`}
                      >
                        {def.isSaving ? 'Saving...' : 'Delete'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Confirm Delete</h3>
            <div className="mb-4">
              <p className="text-gray-700 mb-2">
                You are about to delete override definition:{' '}
                <span className="font-semibold text-red-600 font-mono">{deleteTarget?.key}</span>
              </p>
              <p className="text-sm text-gray-600 mb-4">
                This action cannot be undone. Any workflows using this override will be affected.
              </p>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter delete confirmation password:
              </label>
              <input
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && confirmDelete()}
                placeholder="Enter password"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                autoComplete="new-password"
                autoFocus
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <NotificationModal
        isOpen={showNotification}
        onClose={() => setShowNotification(false)}
        message={notificationMessage}
        type={notificationType}
      />
    </div>
  );
};

export default OverrideDefinitionsManager;
